import OpenAI from 'openai';
import { env } from './env';

const openai = new OpenAI({
  apiKey: env.openai.apiKey,
  dangerouslyAllowBrowser: true
});

export const MAX_RETRIES = 3;
export const RETRY_DELAY = 1000;

interface UserData {
  name: string;
  dob: string;
  birthTime: string;
  birthPlace: string;
}

interface ReadingOptions {
  readingType: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'career' | 'love' | 'health' | 'life';
  userData: UserData;
}

function constructPrompt(options: ReadingOptions): string {
  const { readingType, userData } = options;
  const { name, dob, birthTime, birthPlace } = userData;
  
  const basePrompt = `You are an experienced astrologer named Pandit Ji. Provide an astrological reading for ${name}, born on ${dob} at ${birthTime} in ${birthPlace}. Consider the birth time in the context of the local time zone of ${birthPlace}. `;
  
  const readingPrompts = {
    daily: `${basePrompt} Give a detailed daily horoscope for today. Include predictions about mood, energy levels, favorable activities, and challenges to watch out for. End with a mantra for the day.`,
    weekly: `${basePrompt} Provide a comprehensive weekly horoscope. Break it down into key areas: general outlook, career, relationships, and health. Highlight favorable days and challenging periods. Suggest remedies for any challenging aspects.`,
    monthly: `${basePrompt} Create a detailed monthly forecast. Cover career prospects, financial outlook, relationship dynamics, and health considerations. Identify key dates and planetary transits affecting the native this month. Provide guidance on how to maximize positive energies and mitigate challenges.`,
    yearly: `${basePrompt} Craft a comprehensive yearly forecast. Analyze major planetary transits and their impact on different life areas. Divide the reading into quarters, highlighting significant periods for growth, challenges, and transformation. Include specific guidance for career advancement, financial planning, relationship development, and health maintenance.`,
    career: `${basePrompt} Focus exclusively on career prospects. Analyze current planetary positions affecting professional life. Discuss potential opportunities, challenges, and periods of growth. Provide specific guidance on career decisions, skill development, and timing for job changes or business ventures.`,
    love: `${basePrompt} Create a detailed love and relationship reading. Analyze Venus, Mars, and Moon positions affecting romantic life. For singles, discuss prospects for meeting someone and favorable periods. For those in relationships, provide insights into relationship dynamics, challenges, and growth opportunities. Offer guidance for enhancing relationship harmony.`,
    health: `${basePrompt} Provide a comprehensive health reading. Analyze planetary influences on physical and mental wellbeing. Identify potential vulnerable areas of health and periods requiring extra attention. Suggest specific lifestyle adjustments, preventive measures, and holistic practices to maintain optimal health.`,
    life: `${basePrompt} Offer a profound life prediction reading that examines your entire life journey. Analyze the major planetary configurations in your birth chart to reveal your life purpose, karmic lessons, and spiritual evolution. Cover all major life domains including career path, relationship patterns, financial destiny, and personal growth. Identify key life phases and transformative periods throughout your life span. Provide insights on how to align with your highest destiny and overcome karmic challenges. Include wisdom on fulfilling your life's purpose.`
  };
  
  return readingPrompts[readingType];
}

export async function getAstrologicalReading(options: ReadingOptions): Promise<string> {
  let retries = 0;
  let lastError: any;
  
  while (retries < MAX_RETRIES) {
    try {
      const prompt = constructPrompt(options);
      
      const cachedResponse = sessionStorage.getItem(`reading:${prompt}`);
      if (cachedResponse) {
        return JSON.parse(cachedResponse);
      }
      
      const response = await openai.chat.completions.create({
        model: "gpt-4",
        messages: [
          { 
            role: "system", 
            content: "You are Pandit Ji, an experienced and knowledgeable astrologer with decades of experience in Vedic astrology. You provide accurate, insightful, and personalized astrological readings based on birth details. Your tone is wise, compassionate, and spiritually oriented. You use appropriate Sanskrit terms and references to Hindu mythology when relevant. Your readings are detailed, structured, and include practical guidance."
          },
          { role: "user", content: prompt }
        ],
        temperature: 0.7,
        max_tokens: 1500,
      });
      
      const prediction = response.choices[0]?.message?.content || "Unable to generate reading. Please try again later.";
      
      sessionStorage.setItem(`reading:${prompt}`, JSON.stringify(prediction));
      
      return prediction;
    } catch (error) {
      console.error(`OpenAI API error (attempt ${retries + 1}):`, error);
      lastError = error;
      retries++;
      
      if (retries < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * retries));
      }
    }
  }
  
  throw new Error(`Failed to get astrological reading after ${MAX_RETRIES} attempts: ${lastError.message}`);
}